import { Component, computed, inject, input, resource, signal } from '@angular/core';
import { FileState, parseFile } from './definitions';
import { Api } from '../../../../core/clients/omni-core/api';
import {
  addLoadCurveEntries,
  createDirectLoadCurve,
  getDirectLoadCurve,
  getLoadCurve,
} from '../../../../core/clients/omni-core/functions';
import { HttpErrorResponse } from '@angular/common/http';
import { form, FormField, pattern } from '@angular/forms/signals';
import { HttpError } from '../../../../core/clients/omni-core/models';
import {
  Skeleton,
  InputLabel,
  Dropzone,
  DropzoneFile,
  ToggleLabel,
  InputSimple,
  Toggle,
} from 'excellence-ui';
import { ErrorPage } from '../../../../shared/components/error-page/error-page';
import { Router } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { DecimalPipe } from '@angular/common';
import { FieldErrors } from '../../../../shared/components/field-errors/field-errors';
import { max, min } from '../../../../core/form-validators/min-max.validator';

@Component({
  selector: 'app-energy-interface-load-curve-upload',
  imports: [
    Dropzone,
    TranslocoPipe,
    DropzoneFile,
    DecimalPipe,
    InputLabel,
    InputSimple,
    FormField,
    FieldErrors,
    ToggleLabel,
    Toggle,
    Skeleton,
    ErrorPage,
  ],
  templateUrl: './energy-interface-load-curve-upload.html',
})
export class EnergyInterfaceLoadCurveUpload {
  protected router = inject(Router);
  private api = inject(Api);

  organizationId = input.required<string>();
  energyInterfaceId = input.required<string>();

  protected files = signal<FileState[]>([]);

  protected directLoadCurve = resource({
    params: () => ({
      oid: this.organizationId(),
      energyInterfaceId: this.energyInterfaceId(),
    }),
    loader: async ({ params }) => {
      try {
        return await this.api.invoke(getDirectLoadCurve, params);
      } catch (error) {
        if (!(error instanceof HttpErrorResponse && error.status === 404)) {
          throw error;
        }
        const { id } = await this.api.invoke(createDirectLoadCurve, {
          oid: params.oid,
          body: { energyInterfaceID: params.energyInterfaceId },
        });
        return await this.api.invoke(getLoadCurve, { oid: params.oid, id });
      }
    },
  });

  private formModel = signal({
    showAdvancedOptions: false,
    chunkSize: '1000',
  });

  protected form = form(this.formModel, (schemaPath) => {
    pattern(schemaPath.chunkSize, /^[1-9]\d*$/);
    min(schemaPath.chunkSize, 1);
    max(schemaPath.chunkSize, 2000);
  });

  protected validatedChunkSize = computed(() => {
    const parsed = parseInt(this.formModel().chunkSize, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 1000;
  });

  protected onFileChange(loadCurveId: string, event: Event) {
    const input = event.target as HTMLInputElement;
    const files = input.files;
    if (files && files.length > 0) {
      this.onFileDrop(loadCurveId, files);
    }
  }

  protected async onFileDrop(loadCurveId: string, files: FileList) {
    const fileStates: FileState[] = Array.from(files).map((file) => ({
      id: crypto.randomUUID(),
      file,
      progress: 1,
      error: null,
      loadCurve: null,
      createdAt: new Date(),
      finishedAt: null,
      messageKey: 'cockpit.energyInterfaces.details.loadCurve.fileStatus.reading',
      messageParams: undefined,
    }));
    this.files.update((current) => [...fileStates, ...current]);

    // wait for render to show progress bars before starting processing
    await new Promise((resolve) => setTimeout(resolve, 500));

    // process files in sequence
    for (const state of fileStates) {
      await this.processFile(loadCurveId, state);
    }
  }

  private patchFileState(id: string, patch: Partial<FileState>) {
    this.files.update((current) => current.map((f) => (f.id === id ? { ...f, ...patch } : f)));
  }

  private async processFile(loadCurveId: string, state: FileState) {
    try {
      // parse
      const loadCurve = await parseFile(state);
      this.patchFileState(state.id, { loadCurve, error: null, progress: 5 });
      // upload in chunks
      const chunkSize = this.validatedChunkSize();
      for (let i = 0; i < loadCurve.length; i += chunkSize) {
        const chunk = loadCurve.slice(i, i + chunkSize);

        this.patchFileState(state.id, {
          messageKey: 'cockpit.energyInterfaces.details.loadCurve.fileStatus.uploading',
          messageParams: {
            current: Math.floor(i / chunkSize) + 1,
            total: Math.ceil(loadCurve.length / chunkSize),
          },
        });
        await this.api.invoke(addLoadCurveEntries, {
          oid: this.organizationId(),
          id: loadCurveId,
          body: chunk,
        });
        this.patchFileState(state.id, {
          progress: 5 + Math.floor(((i + chunk.length) / loadCurve.length) * 95),
        });
      }
      this.patchFileState(state.id, {
        messageKey: 'cockpit.energyInterfaces.details.loadCurve.fileStatus.complete',
        messageParams: undefined,
      });
    } catch (error) {
      console.error('Error processing file', { file: state.file.name, error });
      this.patchFileState(state.id, {
        error: error as Error,
        messageKey: 'cockpit.energyInterfaces.details.loadCurve.fileStatus.error',
        messageParams: { error: (error as { error: HttpError })?.error?.message },
      });
    } finally {
      this.patchFileState(state.id, { finishedAt: new Date() });
    }
  }

  protected onClickRemove(fileId: string) {
    this.files.update((current) => current.filter((f) => f.id !== fileId));
  }

  protected async onClickRetry(loadCurveId: string, state: FileState) {
    this.patchFileState(state.id, {
      error: null,
      progress: 1,
      messageKey: 'cockpit.energyInterfaces.details.loadCurve.fileStatus.retrying',
      messageParams: undefined,
    });
    await new Promise((resolve) => setTimeout(resolve, 1000));
    return await this.processFile(loadCurveId, state);
  }
}
